import { useLazyQuery } from "@apollo/client"
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from "react"
import { episode, episodesList } from "../services/graphql/queries"
import { deepCopy, logger, Status } from "../services/utils/utils"
import { MainContext } from "./main"
import { AuthContext } from "./auth"
import { useTranslation } from "react-i18next"
import Episode from "../models/episode"

interface StoriesContextInterface {
  storiesDataLoading: boolean
  storiesList: Episode[]
  storiesListNextToken: string | undefined
  selectedStories: Episode[]
  searchValue: string | null
  setSearchValue: Dispatch<SetStateAction<string | null>>
  setSelectedStories: Dispatch<SetStateAction<Episode[]>>
  loadMoreStories: () => Promise<boolean>
  getStory: (episodeId: string) => Promise<Episode | false>
  getStoriesData: () => Promise<void>
}

const StoriesContext = createContext<StoriesContextInterface>({
  storiesDataLoading: true,
  storiesList: [],
  storiesListNextToken: undefined,
  selectedStories: [],
  searchValue: "",
  setSearchValue: () => {},
  setSelectedStories: () => {},
  loadMoreStories: async () => true,
  getStory: async () => false,
  getStoriesData: async () => {},
})

const StoriesController = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const { team } = useContext(AuthContext)
  const { getInput, setError, setErrorMessage, selectedFilters } =
    useContext(MainContext)

  // states
  const [storiesDataLoading, setStoriesDataLoading] = useState<boolean>(false)
  const [storiesList, setStoriesList] = useState<Episode[]>([])
  const [storiesListNextToken, setStoriesListNextToken] = useState<
    string | undefined
  >()
  const [selectedStories, setSelectedStories] = useState<Episode[]>([])
  const [searchValue, setSearchValue] = useState<string | null>(null)

  // queries
  const [episodesListQuery] = useLazyQuery(episodesList)
  const [episodeQuery] = useLazyQuery(episode)

  // get stories list
  const getStoriesList = async () => {
    try {
      logger(Status.Api, "[STR] QUERY episodesList")

      const input = getInput(team!.id)

      const { data } = await episodesListQuery({
        variables: {
          input: {
            lang: input.lang,
            limit: 50,
            text: searchValue,
            sdg: selectedFilters.find((filter) => filter.type === "sdg")?.id,
            topic: selectedFilters.find((filter) => filter.type === "topic")
              ?.id,
          },
        },
      })

      logger(Status.Info, "[STR] stories list", data.episodesList.items)

      setStoriesList(data.episodesList.items)
      setStoriesListNextToken(data.episodesList.nextToken)

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Stories`)
      setStoriesList([])

      return false
    }
  }

  // load more stories
  const loadMoreStories = async () => {
    try {
      logger(Status.Api, "QUERY episodesList")

      const input = getInput(team!.id)

      const { data } = await episodesListQuery({
        variables: {
          input: {
            lang: input.lang,
            limit: 50,
            text: searchValue,
            nextToken: storiesListNextToken,
            sdg: selectedFilters.find((filter) => filter.type === "sdg")?.id,
            topic: selectedFilters.find((filter) => filter.type === "topic")
              ?.id,
          },
        },
      })
      logger(Status.Info, "[STR] stories list", [
        ...storiesList,
        ...data.episodesList.items,
      ])

      setStoriesList((current) => [...current, ...data.episodesList.items])
      setStoriesListNextToken(data.episodesList.nextToken)

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Stories`)

      return false
    }
  }

  // get story
  const getStory = async (episodeId: string) => {
    try {
      logger(Status.Api, "[STR] QUERY episode")

      const input = getInput(team!.id)

      const { data } = await episodeQuery({
        variables: {
          episodeId,
          lang: input.lang,
        },
      })

      logger(Status.Info, `[STR] story ${episodeId}`, data.episode)

      // parse data
      const episode = deepCopy(data.episode)
      for (const slide of episode.translation.slides) {
        if (slide.embedSlideText) {
          slide.text = slide.embedSlideText
          slide.caption = slide.embedSlideCaption
          slide.captionTitle = slide.embedSlideCaptionTitle
          delete slide.embedSlideText
          delete slide.embedSlideCaption
          delete slide.embedSlideCaptionTitle
        }
        if (slide.fullImageSlideImage) {
          slide.image = slide.fullImageSlideImage
          delete slide.fullImageSlideImage
        }
        if (slide.fullTextSlideText) {
          slide.text = slide.fullTextSlideText
          delete slide.fullTextSlideText
        }
        if (slide.portraitSlideText) {
          slide.text = slide.portraitSlideText
          slide.image = slide.portraitSlideImage
          delete slide.portraitSlideText
          delete slide.portraitSlideImage
        }
        if (slide.quoteSlideImage) {
          slide.image = slide.quoteSlideImage
          delete slide.quoteSlideImage
        }
        if (slide.textImageSlideImage) {
          slide.image = slide.textImageSlideImage
          delete slide.textImageSlideImage
        }
      }

      return episode
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Story`)
      setStoriesList([])

      return false
    }
  }

  // get data
  const getStoriesData = async () => {
    setStoriesDataLoading(true)

    await getStoriesList()

    setStoriesDataLoading(false)
  }

  return (
    <StoriesContext.Provider
      value={{
        storiesDataLoading,
        storiesList,
        storiesListNextToken,
        selectedStories,
        searchValue,
        setSearchValue,
        setSelectedStories,
        loadMoreStories,
        getStory,
        getStoriesData,
      }}
    >
      {children}
    </StoriesContext.Provider>
  )
}
export { StoriesController, StoriesContext }
